import Image from 'next/image';
import { classes } from 'shared';
import hero from '../assets/furniture/hero.jpg';
import { Button } from '../components/general/button';
import { ToolTip } from '../components/general/tooltip';
import { BlueArch, WhiteArch } from '../components/img/svgs';
import { Meta } from '../components/meta/meta';

const pickupUnavailableDates = [
  {
    start: '2024-07-24T00:00:00.000-04:00',
    end: '2024-09-03T00:00:00.000-04:00',
  },
] as const;

const pickupOptions = [
  {
    name: 'Beds for Kids Pickup',
    description: 'Beds for Kids offers pickups based on availability at either 9:30am – 12pm or 1:30pm – 3:30pm.',
    details: [
      '$30/$100 pickup fee depending on location',
      'Scheduled based on availability',
      'All items must be disassembled and easily accessible to our staff',
      'Will only accept items listed above',
      'Pickup fee is tax-deductible',
    ],
    link: {
      href: '/contact#form',
      text: 'Contact Us Now!',
      external: false,
      gated: false,
    },
  },
  {
    name: 'Partner Pickup with ReSupply',
    description: 'ReSupply is a premium service that offers pickups with more flexibility than Beds for Kids offers.',
    details: [
      'Variable pickup fee',
      'Scheduled as soon as two business days',
      'Will perform disassembly and removal of items',
      'Will accept any item in any condition',
      'Pickup fee is NOT tax-deductible, but donated items are',
    ],
    link: {
      href: 'https://donor.resupplyapp.com/spec/charity/555',
      text: 'Schedule Now',
      external: true,
      gated: true,
      gatedUntil: '2024-05-01T00:00:00.000Z',
      gatedText: 'Coming Soon',
    },
  },
] as const;

export default function DonateFurniture() {
  const isPickupUnavailable = pickupUnavailableDates.some(
    date => new Date(date.start) < new Date() && new Date(date.end) > new Date(),
  );

  return (
    <main>
      <Meta title="donate furniture" description="Donate furniture to Beds for Kids" noDefaultFooter />
      <section className="relative h-[70vh] w-full overflow-hidden">
        <Image
          alt="Beds for Kids Corporate Volunteers"
          src={hero}
          className="absolute z-0 h-full w-full object-cover"
          sizes="100vw"
          priority
        />
        <div className="pointer-events-none absolute -bottom-[1px] left-0 right-0">
          <WhiteArch />
        </div>
      </section>
      <section className="bg-white pb-12 text-blue-500">
        <h1 className="mx-auto my-4 max-w-3xl text-center font-operetta text-6xl" id="what-we-take">
          donate furniture
        </h1>
        <p className="mx-auto max-w-3xl px-4 text-center font-avenir text-xl leading-6">
          {new Date() < new Date('2024-09-03T00:00:00.000-04:00') ? (
            <>
              We will be pausing community pickups from Wednesday, July 24th to Tuesday, September 3rd, 2024. We will
              still be accepting drop offs at our warehouse Monday through Friday 8:30am - 4pm. See items we take below.
              Together we are furnishing futures!
            </>
          ) : (
            <>
              Schedule a pickup with us for your furniture donation and know that you&#39;re helping break the cycle of
              furniture poverty by transforming an empty house into a home.{' '}
              <b>We also accept free drop offs at our warehouse Monday through Friday 9:00am - 3:30pm.</b> See items we
              take below. Together we are furnishing futures!
            </>
          )}
        </p>
      </section>
      <section className="flex flex-col items-center bg-white pb-12 text-blue-500">
        <div className="grid max-w-3xl gap-x-12 gap-y-8 px-4 font-avenir md:grid-cols-2">
          <div>
            <h3 className="text-center text-3xl font-medium leading-8 md:text-left">items we take</h3>
            <ul className="ml-12 mt-4 flex list-disc flex-col gap-1">
              <li className="text-lg leading-6">
                Mattresses (Twin, Full, Queen, Toddler)
                <ToolTip
                  title="Asterisk"
                  text="All mattresses must be new or less than 8 years old and must be without stains, rips, pet hair or wear."
                  cClassName="inline-block ml-2"
                />
              </li>
              <li className="text-lg leading-6">Box Springs (Twin, Full, Queen)</li>
              <li className="text-lg leading-6">
                Cribs
                <ToolTip title="Asterisk" text="Drop down cribs are not accepted." cClassName="inline-block ml-2" />
              </li>
              <li className="text-lg leading-6">Dressers</li>
              <li className="text-lg leading-6">Kitchen Tables &amp; Chairs</li>
              <li className="text-lg leading-6">Lamps</li>
              <li className="text-lg leading-6">Sofas, Loveseats, Upholstered Chairs</li>
              <li className="text-lg leading-6">Bunk Beds</li>
              <li className="text-lg leading-6">Tables (Night, Coffee, End)</li>
              <li className="text-lg leading-6">Small Desks &amp; Desk Chairs</li>
              <li className="text-lg leading-6">Small Bookshelves</li>
              <li className="text-lg leading-6">Small TV Stands</li>
              <li className="text-lg leading-6">New Bed Linens</li>
              <li className="text-lg leading-6">New Pillows</li>
              <li className="text-lg leading-6">New Mattress Protectors/Covers</li>
              <li className="text-lg leading-6">New Blankets &amp; Quilts</li>
            </ul>
          </div>
          <div>
            <h3 className="text-center text-3xl font-medium leading-8 md:text-left">items we don&#39;t take</h3>
            <ul className="ml-12 mt-4 flex list-disc flex-col gap-1">
              <li className="text-lg leading-6">Electric/Water/Medical/Futon Beds</li>
              <li className="text-lg leading-6">Day Beds</li>
              <li className="text-lg leading-6">Sleeper Sofas</li>
              <li className="text-lg leading-6">Used Bedding &amp; Pillows</li>
              <li className="text-lg leading-6">TVs/Electronics</li>
              <li className="text-lg leading-6">Trundle Beds</li>
              <li className="text-lg leading-6">Toys &amp; Clothes</li>
              <li className="text-lg leading-6">Lawn &amp; Patio Furniture</li>
              <li className="text-lg leading-6">Large Armoires &amp; China Cabinets</li>
              <li className="text-lg leading-6">Kitchen Items/Household Goods</li>
              <li className="text-lg leading-6">Mirrors</li>
              <li className="text-lg leading-6">Glass-top Tables</li>
              <li className="text-lg leading-6">Loft Bed Frames</li>
            </ul>
          </div>
        </div>
      </section>
      {/* <section className="flex flex-col items-center bg-white pb-12 text-blue-500">
        <p className="max-w-3xl px-4 text-center text-xl">
          Pickups 9:30am &ndash; 12pm or 1:30pm &ndash; 3:30pm Monday - Friday pending availability.
        </p>
      </section> */}
      <BlueArch />
      <section className="bg-blue-500 pb-24 pt-8 text-center text-white" id="schedule">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="font-operetta text-5xl">schedule a pickup</h2>
          <h6 className="mt-8 font-avenir text-2xl font-medium">Have some furniture to donate to Beds for Kids?</h6>
        </div>

        <section className={classes('mx-auto flex-col gap-8 px-4 py-8 md:px-0 lg:gap-12')}>
          <p className="mx-auto mb-6 max-w-3xl px-4 text-center font-avenir text-xl leading-6">
            If you're unable to drop off your furniture, we'd be happy to help arrange a pickup. We offer both Beds for
            Kids pickup services and priority pickups through our partner ReSupply.
          </p>
          <p className="mx-auto mb-6 max-w-3xl px-4 text-center font-avenir text-xl leading-6">
            Simply click the button below and complete the form to see which pickup option is the best fit for you.
          </p>
          <p className="mx-auto mb-16 max-w-3xl px-4 text-center font-avenir text-xl leading-6">
            Every donation stays right here in our community, helping furnish homes for children and families in need
            across our city. Thank you for making a difference!
          </p>
          <Button link="https://donate.resupply.com/charity/555/start?utm_source=widget" bType="secondary-white">
            schedule a pickup
          </Button>
          {/* The script below places a resupply widget floating at the bottom left */}
          {/* <script
            type="text/javascript"
            src="https://widget.resupplyapp.com/prod/loader.js"
            widget_code="d2dccc83fa70cce75905b3ea600de2ab"></script> */}
        </section>
      </section>
    </main>
  );
}

function PickUpOption({ option: { name, description, details, link } }: { option: (typeof pickupOptions)[number] }) {
  return (
    <div className="flex flex-col justify-between gap-4 rounded p-8 ring ring-blue-400">
      <div>
        <h6 className="mb-1 text-2xl font-medium">{name}</h6>
        <p>{description}</p>
      </div>
      <ul className="ml-4 list-disc text-left">
        {details.map(detail => (
          <li key={detail}>{detail}</li>
        ))}
      </ul>
      <div className="mt-4 text-center">
        {link.gated ? (
          new Date(link.gatedUntil) > new Date() ? (
            <Button
              disabled
              bType="secondary-white"
              className="disabled:!bg-transparent disabled:hover:!bg-transparent disabled:hover:!text-white">
              {link.gatedText}
            </Button>
          ) : (
            <Button external={link.external} link={link.href} bType="secondary-white">
              {link.text}
            </Button>
          )
        ) : (
          <Button external={link.external} link={link.href} bType="secondary-white">
            {link.text}
          </Button>
        )}
      </div>
    </div>
  );
}
